import React, { useEffect, useState } from "react";
import { Clock, AlertTriangle, CheckCircle2, X } from "lucide-react";
import { csuiteApi } from "../../../lib/csuiteApi";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "pmv5_csuite_token";
const getToken = () => { try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; } };

/**
 * PilotStatusBanner — countdown + one-click cancel for active Platinum pilots.
 * Renders only when the current user has `subscription.pilot_status === "active"`.
 */
export default function PilotStatusBanner() {
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    csuiteApi.me().then(r => setMe(r?.user || null)).catch(() => setMe(null));
  }, []);

  const sub = me?.subscription || {};
  const isPilot = sub.pilot_status === "active";
  const endsAt = sub.pilot_ends_at ? new Date(sub.pilot_ends_at) : null;

  const daysLeft = endsAt ? Math.max(0, Math.ceil((endsAt.getTime() - Date.now()) / 86400000)) : 0;

  const cancel = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await axios.post(`${API}/api/subscribe/cancel`, {}, { headers: { Authorization: `Bearer ${getToken()}` } });
      setMsg(r.data?.downgraded_to_starter
        ? "Pilot cancelled. No charge. Your account has been downgraded to Starter (free) — everything you built during the pilot stays put."
        : "Subscription cancelled.");
      setConfirm(false);
      setTimeout(() => window.location.reload(), 1800);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Cancellation failed. Please email Requests@Capittance.com.");
    } finally { setBusy(false); }
  };

  if (!isPilot || dismissed) return null;

  const dangerZone = daysLeft <= 2;
  const bg = dangerZone
    ? "linear-gradient(135deg,#FEF2F2 0%,#FEE2E2 100%)"
    : "linear-gradient(135deg,#FEF7ED 0%,#FEF3C7 100%)";
  const border = dangerZone ? "#F87171" : "#F59E0B44";
  const iconColor = dangerZone ? "text-red-700" : "text-amber-700";
  const Icon = dangerZone ? AlertTriangle : Clock;

  return (
    <div className="card p-5 mb-6 relative overflow-hidden" style={{ background: bg, borderColor: border }} data-testid="pilot-banner">
      <button onClick={() => setDismissed(true)} className="absolute top-2 right-2 text-zinc-400 hover:text-zinc-700" title="Hide until next reload" data-testid="pilot-banner-dismiss"><X size={14}/></button>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-sm bg-white/70 grid place-items-center ${iconColor} shrink-0`}><Icon size={16}/></div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-amber-800">14-day Platinum pilot · active</div>
          <div className="font-display text-lg font-extrabold tracking-tight text-zinc-900 mt-0.5" data-testid="pilot-countdown">
            {daysLeft === 0 ? "Pilot ends today" : `${daysLeft} ${daysLeft === 1 ? "day" : "days"} left`}
          </div>
          <div className="text-xs text-zinc-700 mt-1 leading-relaxed">
            {endsAt && <>Pilot ends on <b>{endsAt.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</b>. </>}
            On day 15 your card will be charged <b>$350</b> and the account converts to standard monthly Platinum. Cancel any time before then to avoid the charge.
          </div>
          {msg && <div className="mt-2 text-xs text-emerald-700 flex items-center gap-1.5" data-testid="pilot-cancel-msg"><CheckCircle2 size={12}/> {msg}</div>}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {!confirm ? (
              <button onClick={() => setConfirm(true)} className="btn btn-sm inline-flex items-center gap-1 bg-white hover:bg-zinc-50 text-zinc-700 border border-[var(--border)]" data-testid="pilot-cancel-btn">
                Cancel pilot
              </button>
            ) : (
              <>
                <button onClick={cancel} disabled={busy} className="btn btn-sm bg-red-700 hover:bg-red-800 text-white" data-testid="pilot-cancel-confirm">
                  {busy ? "Cancelling…" : "Yes, cancel my pilot"}
                </button>
                <button onClick={() => setConfirm(false)} className="btn btn-sm bg-white text-zinc-700 border border-[var(--border)]" data-testid="pilot-cancel-back">Never mind</button>
              </>
            )}
            <a href="mailto:Requests@Capittance.com?subject=Platinum%20pilot%20question" className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-800">
              Talk to a human →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
